import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Alert } from '@mui/material';
import { Block as BlockIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useI18nContext } from '@/i18n/i18n-react';

const UnauthorizedPage = () => {
    const { LL } = useI18nContext();
    const location = useLocation();
    const [errorDetails, setErrorDetails] = useState<{
        requiredGroup?: string;
        userGroups?: string[];
        message?: string;
    }>({});

    useEffect(() => {
        // Try to parse error details from query params or state
        const searchParams = new URLSearchParams(location.search);
        const errorParam = searchParams.get('error');

        if (errorParam) {
            try {
                const details = JSON.parse(decodeURIComponent(errorParam));
                setErrorDetails(details);
            } catch (e) {
                // Ignore parse errors
            }
        }
    }, [location]);

    const handleBackToLogin = () => {
        window.location.href = '/';
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                p: 3,
            }}
        >
            <Card sx={{ maxWidth: 600, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
                <CardContent sx={{ p: 4 }}>
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <BlockIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                        <Typography variant="h4" component="h1" gutterBottom>
                            {LL.unauthorized.title()}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            {LL.unauthorized.subtitle()}
                        </Typography>
                    </Box>

                    {/* Error Details */}
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{LL.unauthorized.authStatusSuccess()}:</strong>{' '}
                            {LL.unauthorized.authSuccess()}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{LL.unauthorized.authStatusDenied()}:</strong>{' '}
                            {LL.unauthorized.authDenied()}
                        </Typography>
                    </Alert>

                    {/* Detailed Message */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {errorDetails.message || LL.unauthorized.defaultMessage()}
                        </Typography>

                        {errorDetails.requiredGroup && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>{LL.unauthorized.requiredGroup()}:</strong>{' '}
                                    {errorDetails.requiredGroup}
                                </Typography>
                                {errorDetails.userGroups && errorDetails.userGroups.length > 0 && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        <strong>{LL.unauthorized.yourGroups()}:</strong>{' '}
                                        {errorDetails.userGroups.join(', ')}
                                    </Typography>
                                )}
                            </Alert>
                        )}
                    </Box>

                    {/* Instructions */}
                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {LL.unauthorized.whatCanIDo()}
                        </Typography>
                        <Typography variant="body2" component="div">
                            <ol style={{ margin: 0, paddingLeft: 20 }}>
                                <li>{LL.unauthorized.step1()}</li>
                                <li>{LL.unauthorized.step2()}</li>
                                <li>{LL.unauthorized.step3()}</li>
                                <li>{LL.unauthorized.step4()}</li>
                            </ol>
                        </Typography>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBackToLogin}
                        >
                            {LL.unauthorized.backToLogin()}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default UnauthorizedPage;
